import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { OWNER_DISPLAY_NAME, useServices } from '../../app/services';
import type { BudgetMember } from '../../domain/model';
import { SegmentedControl, SelectField } from '../../ui';

/** A member's name as this person sees it: their own unnamed row reads "You". */
/** The name the server leaves on the member row of someone who deleted their account. */
export const FORMER_MEMBER_NAME = '—';

export function useMemberName() {
  const { t } = useTranslation();
  const { identity } = useServices();
  return (m: BudgetMember | undefined) => {
    if (!m) return t('entry.someone');
    // Someone who deleted their account: the server keeps the row, not the name.
    if (m.displayName === FORMER_MEMBER_NAME) return t('entry.formerMember');
    if (m.userId === identity.session.userId && m.displayName === OWNER_DISPLAY_NAME) return t('sharing.youDefault');
    return m.displayName;
  };
}

/**
 * Choosing a person (or "Shared"): one tap on a few chips in a small
 * household, a list when there are more people than fit in a row.
 */
export function PersonField({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const id = useId();
  if (options.length > 4) {
    return (
      <SelectField label={label} hint={hint} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </SelectField>
    );
  }
  return (
    <div className="stack stack-xs">
      <p className="field-label" id={id}>
        {label}
      </p>
      <SegmentedControl label={label} value={value} onChange={onChange} options={options} />
      {hint && <p className="field-msg">{hint}</p>}
    </div>
  );
}

/** Paid by and belongs to, for a budget with more than one person. */
export function PeopleFields({
  members,
  paidBy,
  belongsTo,
  onPaidBy,
  onBelongsTo,
}: {
  members: BudgetMember[];
  paidBy: string;
  /** 'shared' or a member id. */
  belongsTo: string;
  onPaidBy: (id: string) => void;
  onBelongsTo: (value: string) => void;
}) {
  const { t } = useTranslation();
  const name = useMemberName();
  if (members.length < 2) return null;
  const people = members.map((m) => ({ value: m.id, label: name(m) }));
  return (
    <>
      <PersonField label={t('entry.paidBy')} value={paidBy} options={people} onChange={onPaidBy} />
      <PersonField
        label={t('entry.belongsTo')}
        hint={t('entry.belongsToHint')}
        value={belongsTo}
        options={[{ value: 'shared', label: t('entry.shared') }, ...people]}
        onChange={onBelongsTo}
      />
    </>
  );
}
